import React from "react";
import { AlertTriangle, MessageCircleQuestion } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { formatStrategyRdPct, formatStrategyRdRMultiple } from "../../../utils/strategyRd";
import { strategyRdOdds, type StrategyRdReport, type StrategyRdYearRow } from "../../../utils/strategyRdReport";
import { PNL_NEGATIVE_COLOR, PNL_POSITIVE_COLOR } from "../Statistics/components/statsSignedPnlBarUtils";
import { strategyRdBrandActionButtonClassName } from "./strategyRdCardShell";
import {
  StrategyRdBadge,
  StrategyRdFacts,
  StrategyRdInfo,
  StrategyRdLabCard,
  StrategyRdNote,
  StrategyRdStat,
  StrategyRdStatGrid,
  type StrategyRdTone,
} from "./strategyRdLabUi";

const NA = "N/A";

const signColor = (v: number | null | undefined): string | undefined =>
  v == null ? undefined : v > 0 ? PNL_POSITIVE_COLOR : v < 0 ? PNL_NEGATIVE_COLOR : undefined;

const pct = (v: number | null | undefined): string => formatStrategyRdPct(v) ?? NA;
const rMult = (v: number | null | undefined): string => formatStrategyRdRMultiple(v) ?? NA;

function YearBars({ rows }: { rows: StrategyRdYearRow[] }): JSX.Element {
  const max = Math.max(1, ...rows.map((r) => Math.abs(r.returnPct ?? 0)));
  return (
    <div className="flex h-10 items-center gap-0.5" aria-hidden data-strategy-rd-year-bars="">
      {rows.map((r) => {
        const v = r.returnPct ?? 0;
        const h = Math.max(2, Math.round((Math.abs(v) / max) * 18));
        return (
          <div
            key={r.year}
            className="flex h-full flex-1 flex-col justify-center"
            title={`${r.year}: ${pct(r.returnPct)} · ${r.trades} trades`}
          >
            <div className="flex h-1/2 items-end border-b border-border">
              {v > 0 ? <div className="w-full rounded-t-[2px]" style={{ height: h, background: PNL_POSITIVE_COLOR }} /> : null}
            </div>
            <div className="flex h-1/2 items-start">
              {v < 0 ? <div className="w-full rounded-b-[2px]" style={{ height: h, background: PNL_NEGATIVE_COLOR }} /> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function yearsTone(positive: number, total: number): { tone: StrategyRdTone; label: string } {
  if (positive * 2 > total) return { tone: "good", label: "Mostly up" };
  if (positive * 2 === total) return { tone: "warn", label: "Mixed" };
  return { tone: "bad", label: "Mostly down" };
}

/**
 * What a trader must know before trusting a backtest, in plain words: does it work most
 * years, does it hang on a few lucky trades, what losing runs to sit through, and what a
 * year could look like with half the edge (the planning case). Ask TES explains any of it.
 */
export function StrategyRdRobustnessReport({
  report,
  onAskTes,
}: {
  report: StrategyRdReport | null | undefined;
  onAskTes?: (question: string) => void;
}): JSX.Element | null {
  if (!report) return null;
  const { years, outliers, streaks, monteCarlo, multipleTesting } = report;
  const plan = monteCarlo?.halfEdge ?? monteCarlo?.asTested ?? null;
  const planLabel = monteCarlo?.halfEdge ? "Half the edge" : "The edge as tested";
  const rare = outliers?.verdict === "depends_on_rare_trades";
  // Removing the best trades only tests something when there is a profit to remove.
  const noEdge = outliers?.expectancyR != null && outliers.expectancyR <= 0;
  const mcMonths = monteCarlo?.spanDays != null ? Math.max(1, Math.round(monteCarlo.spanDays / 30.4)) : null;
  const mcShort = mcMonths != null && mcMonths < 12;
  const showYears = Boolean(years && years.total > 0);
  const tiles = [showYears, Boolean(outliers), Boolean(streaks), Boolean(plan)].filter(Boolean).length;
  const columns: 2 | 3 | 4 = tiles >= 4 ? 4 : tiles === 3 ? 3 : 2;

  const yearRows = years?.rows.filter((r) => r.returnPct != null) ?? [];
  const best = yearRows.length > 1 ? yearRows.reduce((a, b) => ((b.returnPct ?? 0) > (a.returnPct ?? 0) ? b : a)) : null;
  const worst = yearRows.length > 1 ? yearRows.reduce((a, b) => ((b.returnPct ?? 0) < (a.returnPct ?? 0) ? b : a)) : null;

  const ask = onAskTes ? (
    <Button
      type="button"
      variant="outline-brand"
      size="sm"
      data-font="primary"
      className={strategyRdBrandActionButtonClassName}
      onClick={() =>
        onAskTes(
          "Explain this backtest's stress test in plain words: the years, whether it depends on a few big trades, the losing streaks and the half-edge planning case. Is it worth trading, and what would you change?"
        )
      }
    >
      <MessageCircleQuestion className="size-4" aria-hidden />
      <span className="normal-case tracking-normal">Ask TES</span>
    </Button>
  ) : null;

  return (
    <StrategyRdLabCard
      title="How bad can it get?"
      description="Losing years, reliance on a few big wins, losing streaks and a simulated year: the ways most backtests break."
      actions={ask}
      data-strategy-rd-report=""
    >
      {multipleTesting && multipleTesting.verdict !== "few" ? (
        <Alert variant="warning" data-strategy-rd-multiple-testing="">
          <AlertTriangle aria-hidden />
          <AlertDescription className="font-[family-name:var(--font-primary)]">{multipleTesting.message}</AlertDescription>
        </Alert>
      ) : null}

      {tiles > 0 ? (
        <StrategyRdStatGrid columns={columns} variant="cards" className="items-start">
          {years && showYears ? (
            <StrategyRdStat
              label="Years"
              info={
                <StrategyRdInfo title="Year by year">
                  <p>
                    The return of each calendar year in the backtest. A strategy worth trading makes money in most
                    years, not one great year carrying several bad ones.
                  </p>
                </StrategyRdInfo>
              }
              badge={
                <StrategyRdBadge tone={yearsTone(years.positive, years.total).tone}>
                  {yearsTone(years.positive, years.total).label}
                </StrategyRdBadge>
              }
              value={`${years.positive}/${years.total}`}
              unit="made money"
              data-strategy-rd-report-years=""
            >
              {years.rows.length > 1 ? <YearBars rows={years.rows} /> : null}
              <StrategyRdFacts
                rows={[
                  best ? { key: "best", label: `Best year (${best.year})`, value: pct(best.returnPct), valueColor: signColor(best.returnPct) } : null,
                  worst ? { key: "worst", label: `Worst year (${worst.year})`, value: pct(worst.returnPct), valueColor: signColor(worst.returnPct) } : null,
                  yearRows.length === 1
                    ? { key: "only", label: String(yearRows[0].year), value: pct(yearRows[0].returnPct), valueColor: signColor(yearRows[0].returnPct) }
                    : null,
                ]}
              />
            </StrategyRdStat>
          ) : null}

          {outliers ? (
            <StrategyRdStat
              label="Big wins"
              info={
                <StrategyRdInfo title="Big wins (outliers)">
                  <p>
                    How much of the profit came from a few unusually large wins. Those are rare and hard to repeat; if
                    removing them wipes out the result, the edge is thin.
                  </p>
                </StrategyRdInfo>
              }
              badge={
                noEdge ? (
                  <StrategyRdBadge tone="neutral">Nothing to test</StrategyRdBadge>
                ) : rare ? (
                  <StrategyRdBadge tone="bad">Relies on them</StrategyRdBadge>
                ) : (
                  <StrategyRdBadge tone="good">Holds up</StrategyRdBadge>
                )
              }
              value={noEdge ? rMult(outliers.expectancyR) : rMult(outliers.expectancyWithoutTop)}
              valueColor={signColor(noEdge ? outliers.expectancyR : outliers.expectancyWithoutTop)}
              unit={noEdge ? "per trade" : "per trade without them"}
              data-strategy-rd-report-outliers=""
            >
              {noEdge ? <span>It already loses per trade, so no big wins are carrying it.</span> : null}
              <StrategyRdFacts
                rows={[
                  { key: "all", label: "All trades", value: rMult(outliers.expectancyR), valueColor: signColor(outliers.expectancyR) },
                  noEdge
                    ? null
                    : {
                        key: "without",
                        label: `Without the best ${outliers.dropped ?? 1}`,
                        value: rMult(outliers.expectancyWithoutTop),
                        valueColor: signColor(outliers.expectancyWithoutTop),
                      },
                ]}
              />
            </StrategyRdStat>
          ) : null}

          {streaks ? (
            <StrategyRdStat
              label="Losing runs"
              info={
                <StrategyRdInfo title="Losing runs">
                  <p>
                    The longest streak of losses in a row, and what to expect going forward. Every strategy has them;
                    knowing the size in advance helps you stick to the plan instead of quitting at the bottom.
                  </p>
                </StrategyRdInfo>
              }
              value={streaks.losingTrades ?? NA}
              unit="losses in a row"
              data-strategy-rd-report-streaks=""
            >
              <StrategyRdFacts
                rows={[
                  { key: "red", label: "Red days in a row", value: streaks.redDays ?? NA },
                  streaks.redDayShare != null
                    ? { key: "share", label: "Days that were red", value: `${Math.round(streaks.redDayShare * 100)}%` }
                    : null,
                  streaks.worstDay?.pct != null
                    ? { key: "worst", label: "Worst day", value: pct(streaks.worstDay.pct), valueColor: PNL_NEGATIVE_COLOR }
                    : null,
                ]}
              />
            </StrategyRdStat>
          ) : null}

          {plan ? (
            <StrategyRdStat
              label="Simulated year"
              info={
                <StrategyRdInfo title="Monte Carlo: the next 12 months">
                  <p>
                    These are not past years. We take the backtest's own trades and deal them out again in a random
                    order, 1,000 times, each time for a full year of trading at the backtest's pace (a{" "}
                    <strong>Monte Carlo</strong> simulation). That shows the range of years you could live through
                    with the same trades, good luck and bad luck included.
                  </p>
                  <p>It can only reuse what the backtest saw: from a few months of data it is a rough guide, not a forecast.</p>
                  <p>"Half the edge" assumes the future is only half as good as the backtest, a sensible safety margin.</p>
                </StrategyRdInfo>
              }
              badge={
                mcShort ? (
                  <StrategyRdBadge tone="warn" title="Less than a year of data behind the simulation.">
                    Rough guide
                  </StrategyRdBadge>
                ) : null
              }
              value={pct(plan.return12m.p50)}
              valueColor={signColor(plan.return12m.p50)}
              unit="median year"
              data-strategy-rd-report-mc=""
            >
              <StrategyRdFacts
                rows={[
                  { key: "assumes", label: "Assumes", value: planLabel },
                  {
                    key: "range",
                    label: "9 in 10 years",
                    value: `${pct(plan.return12m.p5)} to ${pct(plan.return12m.p95)}`,
                  },
                  { key: "drop", label: "A drop over 30%", value: strategyRdOdds(plan.dropOver30) },
                  monteCarlo?.trades != null && mcMonths != null
                    ? {
                        key: "built",
                        label: "Built from",
                        value: `${monteCarlo.trades} trades, ${mcMonths} month${mcMonths === 1 ? "" : "s"}`,
                      }
                    : null,
                ]}
              />
              {mcShort ? (
                <span>Less than a year of data, so treat it as a rough guide. Test a longer period to firm it up.</span>
              ) : null}
            </StrategyRdStat>
          ) : null}
        </StrategyRdStatGrid>
      ) : null}

      {monteCarlo?.note ? <StrategyRdNote>{monteCarlo.note.replace(/half_edge/g, "half the edge")}</StrategyRdNote> : null}
    </StrategyRdLabCard>
  );
}
